import { useState, useEffect } from 'react';
import { X, Save, Loader2, MapPin } from 'lucide-react';
import type { PatrolCheckpoint, CreateCheckpointDTO, UpdateCheckpointDTO } from '@yawi/types';
import { useI18n } from '@/shared/i18n';

interface CheckpointFormProps {
  checkpoint?: PatrolCheckpoint;
  nextOrderIndex?: number;
  onSubmit: (data: CreateCheckpointDTO | UpdateCheckpointDTO) => Promise<void>;
  onCancel: () => void;
  loading?: boolean;
}

export const CheckpointForm = ({
  checkpoint,
  nextOrderIndex = 1,
  onSubmit,
  onCancel,
  loading,
}: CheckpointFormProps) => {
  const { t } = useI18n();
  const isEditing = !!checkpoint;

  const [formData, setFormData] = useState<CreateCheckpointDTO>({
    name: '',
    description: '',
    location: '',
    orderIndex: nextOrderIndex,
  });

  useEffect(() => {
    if (checkpoint) {
      setFormData({
        name: checkpoint.name,
        description: checkpoint.description || '',
        location: checkpoint.location || '',
        orderIndex: checkpoint.orderIndex,
        latitude: checkpoint.latitude || undefined,
        longitude: checkpoint.longitude || undefined,
      });
    }
  }, [checkpoint]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name.trim()) return;
    await onSubmit(formData);
  };

  const getCurrentLocation = () => {
    if ('geolocation' in navigator) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setFormData(prev => ({
            ...prev,
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
          }));
        },
        (error) => {
          console.error('Error getting location:', error);
        }
      );
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-slate-200">
          <h2 className="text-lg font-semibold text-slate-800">
            {isEditing ? t('patrol.editCheckpoint') : t('patrol.newCheckpoint')}
          </h2>
          <button
            onClick={onCancel}
            className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-slate-500" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          {/* Name */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              {t('common.name')}
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
              className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              placeholder={t('patrol.checkpointNamePlaceholder')}
              required
              maxLength={255}
            />
          </div>

          {/* Location */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              {t('common.location')} ({t('common.optional')})
            </label>
            <input
              type="text"
              value={formData.location || ''}
              onChange={(e) => setFormData(prev => ({ ...prev, location: e.target.value || undefined }))}
              className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              placeholder={t('patrol.locationPlaceholder')}
              maxLength={255}
            />
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              {t('common.description')} ({t('common.optional')})
            </label>
            <textarea
              value={formData.description || ''}
              onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
              className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 resize-none"
              placeholder={t('patrol.descriptionPlaceholder')}
              rows={2}
              maxLength={500}
            />
          </div>

          {/* Order Index */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              {t('patrol.orderIndex')} ({t('common.optional')})
            </label>
            <input
              type="number"
              value={formData.orderIndex ?? ''}
              onChange={(e) => setFormData(prev => ({ ...prev, orderIndex: e.target.value ? parseInt(e.target.value) : undefined }))}
              className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              min={0}
              placeholder={t('patrol.autoAssigned')}
            />
          </div>

          {/* GPS Coordinates */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              {t('patrol.gpsCoordinates')} ({t('common.optional')})
            </label>
            <div className="flex gap-2">
              <input
                type="number"
                step="any"
                value={formData.latitude || ''}
                onChange={(e) => setFormData(prev => ({ ...prev, latitude: parseFloat(e.target.value) || undefined }))}
                className="flex-1 px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                placeholder={t('patrol.latitude')}
              />
              <input
                type="number"
                step="any"
                value={formData.longitude || ''}
                onChange={(e) => setFormData(prev => ({ ...prev, longitude: parseFloat(e.target.value) || undefined }))}
                className="flex-1 px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                placeholder={t('patrol.longitude')}
              />
              <button
                type="button"
                onClick={getCurrentLocation}
                className="px-3 py-2 bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200 transition-colors"
                title={t('patrol.useCurrentLocation')}
              >
                <MapPin className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onCancel}
              className="flex-1 px-4 py-2 border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50 transition-colors"
              disabled={loading}
            >
              {t('common.cancel')}
            </button>
            <button
              type="submit"
              disabled={loading || !formData.name.trim()}
              className="flex-1 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {loading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Save className="w-4 h-4" />
              )}
              {t('common.save')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
